import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Modal from './Modal.jsx';
import { indexSiteRooms, searchSiteRooms, resolveSiteRoom } from '../engine/siteRoomSearch.js';

const PAGE_SIZE = 30;

/** Navigation only: no geometry, revision, recovery-draft or Undo changes. */
export default function SiteRoomSearch({ store, disabled, onOpenRoom, onClose, returnFocusRef }) {
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [error, setError] = useState('');
  const errorRef = useRef(null), nextRef = useRef(null), focusNext = useRef(false);
  const index = useMemo(() => {
    try { return indexSiteRooms(store.toDocument()); }
    catch { return null; }
  }, [store, store.rev]);
  const result = useMemo(() => {
    if (!index) return { rooms: [], error: 'The rooms in this Site could not be read. Open its floors individually.' };
    try { return { rooms: searchSiteRooms(index, query), error: '' }; }
    catch (e) { return { rooms: [], error: e.message }; }
  }, [index, query]);
  useLayoutEffect(() => { if (focusNext.current) { nextRef.current?.focus(); focusNext.current = false; } }, [limit]);
  useEffect(() => { if (error) errorRef.current?.focus(); }, [error]);
  function openRoom(room) {
    try {
      if (disabled) throw new Error('Wait for the current operation to finish before opening a room.');
      const current = resolveSiteRoom(store.toDocument(), room);
      onOpenRoom(current);
    } catch (e) { setError(e.message); if (error === e.message) errorRef.current?.focus(); }
  }
  return <Modal open className="room-shell-dialog" width={720} focusKey="room-search" returnFocusRef={returnFocusRef} onClose={onClose}>
    <div className="dhead"><h2>Find a room</h2></div>
    <div className="dbody design-fields" style={{ overflowWrap: 'anywhere' }}>
      <p>Search room, building or floor names across this Site, including upper floors and basements. Results include unsaved changes.</p>
      <p className="hint">Open in 2D selects and frames the room. It keeps your edits and Undo history; it does not save or move anything.</p>
      <label>Search rooms<input type="search" aria-label="Search rooms" value={query} maxLength={200} placeholder="Room, building or floor name" onChange={e => { setQuery(e.target.value); setLimit(PAGE_SIZE); setError(''); }} /></label>
      {index && !index.complete && <p role="alert">Some rooms could not be searched. Review the unavailable entries and diagnostics, or open their floors individually.</p>}
      {result.error && <p role="alert">{result.error}</p>}
      {error && <p ref={errorRef} tabIndex={-1} role="alert">{error}</p>}
      {!result.error && <p role="status">{result.rooms.length} rooms match. Showing {Math.min(limit, result.rooms.length)}.</p>}
      {!result.error && !result.rooms.length && <p>{query.trim() ? 'No rooms match this search. Try a room, building or floor name.' : 'No indoor room outlines are available. Create or scan a room first.'}</p>}
      <div className="design-fields" aria-label="Matching rooms">
        {result.rooms.slice(0, limit).map((room, i) => <section className="design-card" key={room.key} aria-label={room.displayName}>
          <h3 ref={i === limit - PAGE_SIZE ? nextRef : undefined} tabIndex={-1}>{room.displayName}</h3>
          {room.disabled && <p>{room.reason || 'This room cannot be opened safely. Review its floor in the editor.'}</p>}
          <button className="btn" disabled={disabled || room.disabled} onClick={() => openRoom(room)}>Open in 2D</button>
        </section>)}
      </div>
      {limit < result.rooms.length && <button className="btn" onClick={() => { focusNext.current = true; setLimit(value => value + PAGE_SIZE); }}>Show next {Math.min(PAGE_SIZE, result.rooms.length - limit)} rooms</button>}
      {!!index?.diagnostics?.length && <details><summary>Search diagnostics ({index.diagnostics.length})</summary><ul>{index.diagnostics.map(d => <li key={d.key}>{d.message}</li>)}</ul></details>}
    </div>
    <div className="dfoot"><button className="btn" onClick={onClose}>Close</button></div>
  </Modal>;
}
